import usePageVisibilityChange from '@src/hooks/usePageVisibilityChange';
import { useVideoCreatorStore } from '@src/stores-v2';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import { STALE_VIDEO_DETECTED_EVENT } from '@src/utility/staleVideoEvents';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { ConfirmModal } from './common/ConfirmModal';
import { CopyIcon } from './common/icons';
import Modal from './common/Modal';
import CopyContentModal from './stage/CopyContentModal';

import type { CurrentLoadedVideo } from '@src/stores/VideoCreatorStore';
import type { Video, VideoItem, VideoVersion } from '@src/types.ts/story';

const NewVideoVersionListener = observer(() => {
  const videoCreator = useVideoCreatorStore();

  const [newVideoVersionModal, setNewVersionModal] = useState<{
    type: 'save_as' | 'confirm';
    video: {
      id: Video['id'];
      title: Video['title'];
      lastActionJson: Video['lastActionJson'];
    };
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleNewVideoVersion = (
    currentVideo: CurrentLoadedVideo,
    newVersion: VideoVersion,
  ) => {
    setNewVersionModal({
      type: 'confirm',
      video: {
        id: currentVideo.id,
        title: currentVideo.title,
        lastActionJson: newVersion.lastActionJson,
      },
    });
  };

  const duplicateOutdatedCurrentVideo = async (): Promise<VideoItem> => {
    await videoCreator.prepareCurrentVideoForSave(!!videoCreator.renderer);

    let videoData: { id: string; slug: string; hash: string };
    videoData =
      await videoCreator.datoClientStore.videoRepository.saveOrUpdateVideo({
        ...videoCreator.currentVideo,
        transcriptionText:
          videoCreator.rootStore.transcriptionStore.getTranscriptionText(),
        transcriptionSnapshot: {
          elements:
            videoCreator.rootStore.transcriptionStore.getFinalTranscriptionElements(),
        },
        id: undefined, // create a new video
      });
    const copy = await videoCreator.datoClientStore.videoRepository.findVideo(
      videoData.id,
    );
    if (!copy) {
      throw new Error('Failed to copy outdated current video');
    }
    return copy;
  };

  const handleSave = async (storyName: string) => {
    if (!newVideoVersionModal) {
      return;
    }
    const { video } = newVideoVersionModal;
    if (video.id) {
      setIsSaving(true);
      try {
        await videoCreator.copyVideo(
          // cannot just pass videoId because it will copy the latest version instead of the current one
          await duplicateOutdatedCurrentVideo(),
          storyName,
          'creator-studio',
        );
      } catch (error) {
        console.error('Error saving outdated video copy:', error);
      } finally {
        setIsSaving(false);
      }
    } else {
      console.error(
        'Error saving outdated video copy: incomplete video object',
        video,
      );
    }
    setNewVersionModal(null);
  };

  const handleConfirm = () => {
    setNewVersionModal(prev => ({
      ...prev!,
      type: 'save_as',
    }));
  };

  const closeModal = () => {
    setNewVersionModal(null);
  };

  usePageVisibilityChange({
    active: [
      () => {
        videoCreator.refreshManager.handleCurrentVideoVersion(
          handleNewVideoVersion,
        );
      },
    ],
  });

  // PLAT-11911: a clip opened without a load differs from Dato in content.
  useEffect(() => {
    const showNewerVersion = (event: Event) => {
      const { currentVideo } = videoCreator;
      if (!currentVideo) return;
      handleNewVideoVersion(
        currentVideo,
        (event as CustomEvent<VideoVersion>).detail,
      );
    };
    window.addEventListener(STALE_VIDEO_DETECTED_EVENT, showNewerVersion);
    return () =>
      window.removeEventListener(STALE_VIDEO_DETECTED_EVENT, showNewerVersion);
  }, [videoCreator]);

  if (newVideoVersionModal?.type === 'confirm') {
    return (
      <ConfirmModal
        closeModal={closeModal}
        onConfirm={handleConfirm}
        onCancel={closeModal}
        confirmText={
          <>
            <CopyIcon />
            Save As
          </>
        }
        cancelText="Close"
        icon={
          <CircleExclammationIcon
            width="16"
            height="16"
            strokeColor="var(--destructive)"
          />
        }
        title="You May Be Overwriting Changes"
        message={`${
          newVideoVersionModal?.video?.lastActionJson?.editor || 'Someone'
        } recently saved this story. Refresh your page to view their changes, or save a new copy.`}
        accentColor="var(--primary)"
      />
    );
  }

  if (newVideoVersionModal?.type === 'save_as') {
    return (
      <Modal isOpen closeModal={closeModal}>
        <CopyContentModal
          name={newVideoVersionModal.video?.title}
          tag="Story"
          type={newVideoVersionModal.type}
          onCancel={closeModal}
          onSave={handleSave}
          modalOverrides={{
            wrapperStyles: isSaving
              ? {
                  'pointer-events': 'none',
                }
              : undefined,
            saveButtonContent: isSaving ? (
              <>
                <CopyIcon /> Saving...
              </>
            ) : undefined,
          }}
        />
      </Modal>
    );
  }

  return null;
});

export default NewVideoVersionListener;
